import React from 'react';

// Object kinds of a profiled source (GRAPH_API v3, `attributes.objectKind`). A Salesforce profile
// holds every queryable object, each with its kind; a node without one — every relational table
// and Mongo collection — is DATA. Lists and pickers show EVERY kind until the user turns one off:
// hiding kinds by default kept objects people must pick (RecordType, Skill, …) out of the lists.
export const OBJECT_KINDS = [
  ['DATA', 'Data'],
  ['CONFIGURATION', 'Configuration'],
  ['SETUP', 'Setup'],
  ['DERIVED', 'Share / History / Feed'],
  ['METADATA', 'Custom settings / metadata'],
  ['EXTERNAL', 'External'],
  ['BIG', 'Big objects'],
];

const LABELS = Object.fromEntries(OBJECT_KINDS);

// What each kind holds, for the filter row's hover text.
const KIND_HELP = {
  DATA: 'business records: Account, Contact, Case, custom objects',
  CONFIGURATION: 'reference data a provision matches by name in the target org: RecordType, Profile, UserRole, Skill, …',
  SETUP: 'the rest of the org\'s setup objects',
  DERIVED: 'objects Salesforce keeps beside another object: AccountShare, AccountHistory, AccountFeed, change events',
  METADATA: 'custom settings and custom metadata types (__mdt)',
  EXTERNAL: 'external objects (__x)',
  BIG: 'big objects (__b)',
};

export const objectKindOf = (node) => {
  const k = node && node.attributes ? node.attributes.objectKind : null;
  return k ? String(k).toUpperCase() : 'DATA';
};

export const objectKindLabel = (kind) => LABELS[kind] || kind;

/** { kind: count } over `items`. */
export const kindCounts = (items) => {
  const out = {};
  (items || []).forEach((n) => { const k = objectKindOf(n); out[k] = (out[k] || 0) + 1; });
  return out;
};

/** `items` without the kinds in `hidden` (a Set); every item when nothing is hidden. */
export const withoutHiddenKinds = (items, hidden) => {
  if (!hidden || hidden.size === 0) return items || [];
  return (items || []).filter((n) => !hidden.has(objectKindOf(n)));
};

/**
 * One toggle per kind present in `items`, each with its count, all on until turned off.
 * `hidden` (a Set of kinds) / `onChange(nextSet)`. Renders nothing when the items hold a single
 * kind (a relational source), so it can be dropped next to any object list or picker.
 */
// [kind, label] for every kind in `counts`, known kinds in OBJECT_KINDS order.
const presentKinds = (counts) => {
  const present = OBJECT_KINDS.filter(([k]) => counts[k]);
  Object.keys(counts).forEach((k) => { if (!LABELS[k]) present.push([k, k]); });
  return present;
};

export const KindToggles = ({ items, hidden, onChange, chipClass = 'dg-chip', testId = 'dg-kinds', disabled = false }) => {
  const counts = kindCounts(items);
  const present = presentKinds(counts);
  if (present.length < 2) return null;
  const off = hidden || new Set();
  const toggle = (k) => {
    const next = new Set(off);
    if (next.has(k)) next.delete(k); else next.add(k);
    onChange(next);
  };
  return (
    <span className={`${chipClass}s dg-kinds`} data-testid={testId} role='group' aria-label='Object kinds'>
      {present.map(([k, label]) => (
        <button key={k} type='button' className={`${chipClass}${off.has(k) ? '' : ` ${chipClass}--on`}`} disabled={disabled}
          aria-pressed={!off.has(k)} onClick={() => toggle(k)} data-testid={`${testId}-${k}`}>
          {label} ({counts[k]})
        </button>
      ))}
    </span>
  );
};

/** The hover text of a filter row over `listName`'s list: what turning a kind off does, then each kind present. */
export const kindFilterHelp = (items, listName) => [
  `Turn a kind off to hide its objects from the ${listName} list. Every kind is shown until you do; `
    + 'the filters only narrow the list and never change what this data graph pulls.',
  ...presentKinds(kindCounts(items)).map(([k, label]) => (KIND_HELP[k] ? `${label}: ${KIND_HELP[k]}.` : label)),
].join('\n');

/**
 * KindToggles on a row of their own, labelled "Table list filters", with hover text naming the list they
 * narrow (`listName`). Renders nothing when KindToggles would.
 */
export const KindFilterRow = ({ items, hidden, onChange, listName, testId = 'dg-kinds', disabled = false }) => {
  if (presentKinds(kindCounts(items)).length < 2) return null;
  return (
    <div className='dg-kind-row' title={kindFilterHelp(items, listName)} data-testid={`${testId}-row`}>
      <span className='dg-kind-row__label'>Table list filters</span>
      <KindToggles items={items} hidden={hidden} onChange={onChange} testId={testId} disabled={disabled} />
    </div>
  );
};
